import { useCallback, useEffect, useRef, useState } from 'react';
import { api, openAuthed } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { ViewLink } from '../components/ViewLink.jsx';
import { discard, downscale, enqueueSlip, flushQueue, queued } from '../lib/slipQueue.js';

/**
 * A client never sees the internal enum, and never sees WHY a slip is
 * waiting — `review_reason` is a working note for the practice, not
 * something to hand back to whoever dropped off the receipt.
 */
// The server is the authority on this limit (validated in the upload route);
// this is only a client-side courtesy so "Add another page" disables before
// a doomed 6th photo is even taken.
const MAX_SLIP_PAGES = 5;

const STATUS_LABEL = {
  captured: 'Sent',
  extracted: 'Being read',
  needs_review: 'Waiting for your bookkeeper',
  extraction_failed: 'Waiting for your bookkeeper',
  posted: 'Done',
  rejected: 'Not accepted — ask your bookkeeper',
};

/**
 * The phone camera, and the inbox of what has been sent.
 *
 * The failure that matters is a dead signal in a shop: a slip that fails to
 * upload is a slip that gets thrown away, and the evidence for a VAT claim
 * goes with it. So a captured photo is written to IndexedDB (slipQueue.js)
 * before anything is sent, and every mount, every 'online' event and every
 * new capture tries to flush whatever is still waiting.
 */
export function Slips({ entity, onView }) {
  const [slips, setSlips] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [pending, setPending] = useState(0);
  const [failedItems, setFailedItems] = useState([]);
  const [busy, setBusy] = useState(false);
  // A long slip becomes several photographs before it is one slip. Nothing
  // here reaches IndexedDB until "Send" — this is just what has been
  // photographed so far in this sitting.
  const [pendingPages, setPendingPages] = useState([]);
  const inputRef = useRef(null);

  const canReview = (entity?.capabilities ?? []).includes('slips.review');

  // "Waiting to send" and "rejected" are shown separately: a rejected item
  // will never leave the queue on its own (flushQueue skips it every pass),
  // so folding it into the same counter as items that are simply waiting
  // for signal would hide the fact that one of them needs a human decision.
  //
  // Both counts are scoped to the entity on screen, by the same rule
  // flushQueue uses (an item with no entityId is a pre-scoping record and
  // belongs to whoever is asking). Without that, a practice user viewing
  // client B would be told N slips are waiting to send and then watch that
  // number never move, because those slips belong to client A and this
  // screen will never send them.
  const refreshPending = useCallback(async () => {
    const items = (await queued()).filter((i) => (i.entityId ?? entity?.id) === entity?.id);
    setPending(items.filter((i) => !i.failed).length);
    setFailedItems(items.filter((i) => i.failed));
  }, [entity?.id]);

  const loadSlips = useCallback(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/slips`)
      .then((d) => setSlips(d.slips))
      .catch((e) => setErr(e.message));
  }, [entity?.id]);

  const flush = useCallback(async () => {
    if (!entity?.id) return { sent: 0, failed: 0 };
    try {
      const result = await flushQueue(api, entity.id);
      if (result.sent > 0) loadSlips();
      return result;
    } finally {
      refreshPending();
    }
  }, [entity?.id, loadSlips, refreshPending]);

  // Flush on mount (a page load is itself a good moment to check for signal)
  // and whenever the browser tells us we are back online.
  useEffect(() => { loadSlips(); refreshPending(); flush(); }, [entity?.id]); // eslint-disable-line

  useEffect(() => {
    const onOnline = () => flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flush]);

  // Revoke every still-open pending-page preview URL on unmount. Removing or
  // sending a page revokes its own URL immediately (below); this only
  // catches whatever is left if the component goes away mid-capture.
  const pendingPagesRef = useRef(pendingPages);
  pendingPagesRef.current = pendingPages;
  useEffect(() => () => pendingPagesRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  const capturePage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same photo (or another) be picked again
    if (!file) return;

    setErr(null); setMsg(null); setBusy(true);
    try {
      const { blob, width, height } = await downscale(file);
      const page = { id: crypto.randomUUID(), blob, width, height, url: URL.createObjectURL(blob) };
      // The cap is enforced HERE, in the updater, not only by the button's
      // `disabled` below. The button is a courtesy; this is the invariant.
      // A page turned away never becomes state, so its preview URL is
      // revoked on the spot rather than leaking (revoking twice is a no-op,
      // which is what makes doing it in the updater safe).
      setPendingPages((current) => {
        if (current.length >= MAX_SLIP_PAGES) { URL.revokeObjectURL(page.url); return current; }
        return [...current, page];
      });
      if (pendingPages.length >= MAX_SLIP_PAGES) {
        setErr(`A slip can be at most ${MAX_SLIP_PAGES} pages — send these and start another.`);
      }
    } catch (e) {
      // downscale itself threw — nothing was captured, so this photo is
      // genuinely gone and retaking it is the only fix. The rest of the
      // pending pages (if any) are untouched.
      setErr(`That photo could not be saved — please take it again. (${e.message})`);
    } finally {
      setBusy(false);
    }
  };

  const removePendingPage = (id) => {
    setPendingPages((pages) => {
      const gone = pages.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return pages.filter((p) => p.id !== id);
    });
  };

  const send = async () => {
    if (!entity?.id || pendingPages.length === 0) return;
    setErr(null); setMsg(null); setBusy(true);
    const pages = pendingPages;
    try {
      // The entity goes INTO the queue record. A queued slip can outlive this
      // screen — the practice user switches client, the screen remounts and
      // flushes — and only the entity stored here keeps the photograph out of
      // the wrong client's ledger.
      await enqueueSlip({
        entityId: entity.id,
        pages: pages.map(({ blob, width, height }) => ({ blob, width, height })),
      });
      // Only now has anything reached IndexedDB — safe to clear the capture
      // session and let the thumbnails go.
      pages.forEach((p) => URL.revokeObjectURL(p.url));
      setPendingPages([]);
      await refreshPending();

      // Try to send it straight away; if there is no signal this simply
      // leaves it queued rather than failing the capture.
      const { sent, failed } = await flush();
      if (failed > 0) setMsg('Saved. One of your queued slips was rejected — see below.');
      else if (sent > 0) setMsg('Sent.');
      else setMsg('Saved on this device — it will send once you have signal.');
    } catch (e) {
      // enqueueSlip threw before anything reached IndexedDB, so these
      // photographs never became a queued slip — they are genuinely gone.
      setErr(`That slip could not be saved — please take it again. (${e.message})`);
      pages.forEach((p) => URL.revokeObjectURL(p.url));
      setPendingPages([]);
      await refreshPending();
    } finally {
      setBusy(false);
    }
  };

  const removeFailed = async (clientRef) => {
    await discard(clientRef);
    await refreshPending();
  };

  const viewImage = (slip) => openAuthed(`/api/slips/${slip.id}/image`).catch((e) => setErr(e.message));

  return (
    <>
      <ReportHead entity={entity} title="Slips"
                  sub="Photograph a receipt or invoice — it is kept on this phone even if the signal is down." />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {failedItems.length > 0 && (
        <div className="error">
          <strong>
            {failedItems.length} slip{failedItems.length > 1 ? 's' : ''} could not be sent:
          </strong>
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {failedItems.map((item) => (
              <li key={item.clientRef} style={{ marginBottom: 6 }}>
                {item.failedReason ?? 'Rejected by the server.'}{' '}
                <button className="icon-btn" onClick={() => removeFailed(item.clientRef)}>Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
             onChange={capturePage} style={{ display: 'none' }} aria-label="Photograph a slip page" />

      {pendingPages.length > 0 && (
        <div className="controls" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pendingPages.map((p, i) => (
              <div key={p.id} style={{ textAlign: 'center' }}>
                <img src={p.url} alt={`Page ${i + 1} of ${pendingPages.length}`}
                     style={{ width: 72, height: 96, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  Page {i + 1} of {pendingPages.length}
                </div>
                <button className="icon-btn" onClick={() => removePendingPage(p.id)} disabled={busy}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="controls" style={{ alignItems: 'center', gap: 16 }}>
        <button className="btn" onClick={() => inputRef.current?.click()}
                disabled={busy || pendingPages.length >= MAX_SLIP_PAGES}>
          {busy ? 'Saving…'
            : pendingPages.length === 0 ? 'Photograph a slip'
            : `Add another page (${pendingPages.length}/${MAX_SLIP_PAGES})`}
        </button>
        {pendingPages.length > 0 && (
          <button className="btn" onClick={send} disabled={busy}>
            Send
          </button>
        )}
        {pending > 0 && (
          <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
            {pending} waiting to send
          </span>
        )}
        {canReview && (
          <ViewLink className="btn btn--quiet" view="slip-review" entityId={entity.id}
                        onNavigate={() => onView('slip-review')}>
            Review queue
          </ViewLink>
        )}
      </div>

      {slips === null ? (
        <div className="empty">Loading…</div>
      ) : slips.length === 0 ? (
        <Empty>
          <p>Nothing captured yet.</p>
          <p>Photograph a receipt or invoice to get started.</p>
        </Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Date</th>
                <th>Supplier</th>
                <th className="num" style={{ width: 120 }}>Total</th>
                <th style={{ width: 210 }}>Status</th>
                <th style={{ width: 70 }} />
              </tr>
            </thead>
            <tbody>
              {slips.map((s) => (
                <tr key={s.id}>
                  <td className="figure">{s.slipDate ?? '—'}</td>
                  <td>{s.supplierName ?? '—'}</td>
                  <td className="num"><Money value={s.gross} /></td>
                  <td>{STATUS_LABEL[s.status] ?? s.status}</td>
                  <td className="num">
                    <button className="icon-btn" onClick={() => viewImage(s)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note">
        A photo you take here is saved on this device the moment you take it, so losing signal or
        turning off data will not lose the slip — it sends itself as soon as you are back online.
      </div>
    </>
  );
}
